import { useEffect, useState } from 'react';

import { useInView } from 'react-intersection-observer';

import { useAppDispatch } from 'dux/app/hooks';

import { theme } from '@prose-ui';
import { keyframes, legacyTheme, styled } from '@prose-ui/legacy';

import Spacer from 'Components/Spacer';
import { Typography } from 'Components/Typography';

import { BackgroundVideo } from 'Blocks/BackgroundVideo';

import { clinicalClaimsContent as content } from 'assets/content/landingPages/clinicalClaims';

import useMedia from 'utils/useMedia';
import useResponsiveVariant from 'utils/useResponsiveVariant';

import { trackHeapEvent } from 'dux/tracking/actions';

// SECTION WRAPPER
const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto auto;
  grid-gap: ${theme.spacing['10x']};
  width: 100%;
  padding: ${theme.spacing['5x']} ${theme.spacing['5x']} ${theme.spacing['10x']};

  ${legacyTheme.breakpoints.up('sm')} {
    grid-gap: ${theme.spacing['14x']};
    padding: ${theme.spacing['10x']};
  }

  ${legacyTheme.breakpoints.up('md')} {
    grid-gap: ${theme.spacing['25x']};
    grid-template-columns: auto 60%;
    grid-template-rows: auto;
    padding: ${theme.spacing['12x']} ${theme.spacing['15x']};
  }

  ${legacyTheme.breakpoints.up('lg')} {
    grid-gap: ${theme.spacing['30x']};
    grid-template-columns: auto 65%;
  }
`;

// VIDEO
const VideoWrapper = styled.div`
  grid-column: 1 / 2;

  ${legacyTheme.breakpoints.up('md')} {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  & video {
    border-radius: 10px;
  }
`;

// NUMBERS WRAPPER
const TextAndGaugeWrapper = styled.div`
  display: grid;
  gap: ${theme.spacing['12x']};
  max-width: 330px;

  ${legacyTheme.breakpoints.up('sm')} {
    max-width: 650px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    display: flex;
    grid-column: 2 / 2;
    grid-row: 1 / -1;
    flex-direction: column;
    gap: ${theme.spacing['10x']};
    justify-content: center;

    max-width: none;
  }
`;

// TITLE
const Title = styled(Typography)`
  color: ${theme.colors.primary[300]};
`;

// NUMBERS GRID
const NumbersWrapper = styled.div`
  display: grid;
  grid-template-rows: auto auto auto;
  grid-template-columns: 1fr;
  grid-gap: ${theme.spacing['6x']};

  ${legacyTheme.breakpoints.up('sm')} {
    grid-gap: ${theme.spacing['8x']};
  }

  ${legacyTheme.breakpoints.up('md')} {
    grid-gap: ${theme.spacing['10x']};
    grid-template-columns: 1fr 1fr;
  }

  ${legacyTheme.breakpoints.up('lg')} {
    grid-gap: ${theme.spacing['15x']};
  }
`;

const NumberWrapper = styled.div`
  display: grid;
  grid-template-columns: 92px auto;
  grid-gap: ${theme.spacing['5x']};
`;

const GaugeWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  align-items: center;
  height: 5px;
`;

// PROGRESS BAR ANIMATION
const progressAnimation = (number: number) => keyframes`
  from {
    width: 0%;
  }
  to {
    width: ${number}%;
  }
`;

const GaugeTop = styled.div<{ number: number }>`
  grid-area: 1 / 1;
  max-width: ${({ number }) => number}%;
  height: 100%;

  border-radius: 5px;
  background: ${theme.colors.primary[300]};
  animation: ${({ number }) => progressAnimation(number)} 2s ease-in-out;
`;

const GaugeUnder = styled.div`
  grid-area: 1 / 1;
  width: 100%;
  height: 50%;

  background: ${theme.colors.primary[200]};
`;

const Number = styled(Typography)`
  color: ${theme.colors.primary[300]};
`;

const Label = styled(Typography)`
  color: ${theme.colors.primary[300]};
`;

// SOURCES SECTION
const SourceButton = styled(Typography)`
  cursor: pointer;
  text-decoration: underline;
  color: ${theme.colors.primary[300]};
`;

const SourcesSection = styled.div`
  padding: ${theme.spacing['10x']} ${theme.spacing['8x']};
  background-color: ${theme.colors.primary[300]};

  ${legacyTheme.breakpoints.up('md')} {
    padding: ${theme.spacing['20x']};
  }
`;

export const ClinicalClaims = () => {
  const dispatch = useAppDispatch();
  const [numbersRef, numbersInView] = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });
  const [videoRef, videoInView] = useInView({ initialInView: false });
  const isTablet = useMedia(`(min-width: 767px) and (max-width: 1023px)`);
  const { isMobile } = useResponsiveVariant();
  const [isOpen, setIsOpen] = useState(false);
  const [shouldPlayVideo, setShouldPlayVideo] = useState(false);

  useEffect(() => {
    if (videoInView) {
      setShouldPlayVideo(true);
    } else {
      setShouldPlayVideo(false);
    }
  }, [videoInView]);

  const { numbers, sources, title, video } = content;
  const filteredSources = isMobile ? sources.filter((_source, i) => i < 4) : sources;
  const filteredNumbers = isMobile
    ? numbers.filter(({ displayOnMobile }) => displayOnMobile)
    : numbers;

  return (
    <>
      <Wrapper data-testid="skincare-lp-clinical-claims">
        <VideoWrapper ref={videoRef}>
          <BackgroundVideo
            autoplayDelay={1000}
            forcePause={!shouldPlayVideo}
            imageAlt={video.alt}
            onPause={() => dispatch(trackHeapEvent('Skincare LP clinical claims video paused'))}
            onPlay={() => dispatch(trackHeapEvent('Skincare LP clinical claims video played'))}
            posterImages={video.poster}
            videoSources={video.videoSrc}
          />
        </VideoWrapper>

        <TextAndGaugeWrapper>
          <Title markupName="h2" variant={isTablet ? 'hero1' : 'h1r'}>
            {title}
          </Title>

          <NumbersWrapper ref={numbersRef}>
            {filteredNumbers.map(({ key, number, numberLabel }) => (
              <div key={key}>
                <GaugeWrapper>
                  <GaugeUnder />
                  {numbersInView && <GaugeTop number={number} />}
                </GaugeWrapper>
                <Spacer size={24} />
                <NumberWrapper>
                  <Number markupName="p" variant="hero2">
                    {number}%
                  </Number>

                  <Label variant="p1">{numberLabel}</Label>
                </NumberWrapper>
              </div>
            ))}
          </NumbersWrapper>

          <SourceButton onClick={() => setIsOpen((prevState) => !prevState)} variant="p2">
            {isOpen ? 'Hide source (-)' : 'Display source (+)'}
          </SourceButton>
        </TextAndGaugeWrapper>
      </Wrapper>

      {isOpen && (
        <SourcesSection>
          <Typography color="white" variant="p2">
            {filteredSources.map((source) => source)}
          </Typography>
        </SourcesSection>
      )}
    </>
  );
};
